import { useState, useCallback } from 'react';
import ConfirmModal from '../components/ConfirmModal';

// One way to ask "are you sure?" across the app.
//
// It replaces `window.confirm`, which had a failure mode worth spelling out:
// the native dialog is suppressed by embedded webviews, and by Chrome itself
// once a user ticks "prevent this page from creating additional dialogs" (which
// then holds for the rest of the session). `confirm()` returns FALSE in those
// cases, so the near-universal
//
//     if (!window.confirm('…')) return;
//
// took the early return and the action died in silence — no delete, no toast,
// no error. Reported as "the delete button does nothing".
//
// Beyond reliability it buys correctness of the prompt itself: the native
// dialog renders LTR with browser-chrome buttons in an RTL Hebrew app, and can
// carry only a string — no amount, no record, no styled danger tone.
//
// Usage:
//   const [askConfirm, confirmDialog] = useConfirm();
//   askConfirm({ title, message, detail, confirmLabel, tone: 'danger', onConfirm: async () => {…} });
//   return (<>…{confirmDialog}</>);
//
// The dialog closes itself before running onConfirm, so callers never repeat
// that bookkeeping — and a slow handler can't leave a dead dialog on screen.
export function useConfirm() {
  const [state, setState] = useState(null);
  const close = useCallback(() => setState(null), []);
  const askConfirm = useCallback((opts) => setState(opts), []);

  const confirmDialog = state ? (
    <ConfirmModal
      {...state}
      onConfirm={async () => { setState(null); await state.onConfirm?.(); }}
      onCancel={close}
    />
  ) : null;

  return [askConfirm, confirmDialog];
}
